"use client"

import {
  graphql,
  readFragment,
  useQuery,
  useSubscription,
} from "@opensea/graphql"
import { useEffect, useMemo, useState } from "react"
import { useAddress } from "../../polychain/hooks"
import { ContextBalanceFragment } from "./ContextBalance.fragment"
import type { BalancesContextType } from "./context"
import { BalancesContext } from "./context"
import type { Balance } from "./types"

const query = graphql(
  `
    query BalancesQuery(
      $address: Address!
      $disableSpamFiltering: Boolean = false
    ) {
      addressBalances(
        address: $address
        disableSpamFiltering: $disableSpamFiltering
      ) {
        ...ContextBalance
      }
    }
  `,
  [ContextBalanceFragment],
)
const queryContext = {
  suspense: false,
}

const subscriptionQuery = graphql(
  `
    subscription BalancesSubscription(
      $address: Address!
      $filter: CurrenciesFilter
    ) {
      currencyOwnershipUpdates(address: $address, filter: $filter) {
        id
        ...ContextBalance
      }
    }
  `,
  [ContextBalanceFragment],
)

export type BalancesProviderProps = {
  initialBalances?: Balance[]
  disableSpamFiltering?: boolean
  children: React.ReactNode
}

export function BalancesProvider({
  initialBalances,
  disableSpamFiltering = false,
  children,
}: BalancesProviderProps) {
  const [balances, setBalances] = useState<Balance[]>(initialBalances ?? [])
  const [loading, setLoading] = useState(initialBalances === undefined)
  const address = useAddress()

  const [{ data }, reexecuteQuery] = useQuery(
    {
      query,
      pause: initialBalances !== undefined || address === undefined,
      context: queryContext,
      variables: {
        address: address ?? "",
        disableSpamFiltering,
      },
    },
    { throwOnError: false },
  )
  useEffect(
    function setFetchedBalances() {
      if (data) {
        setBalances(
          data.addressBalances.map(ab =>
            readFragment(ContextBalanceFragment, ab),
          ),
        )
        setLoading(false)
      }
    },
    [data],
  )

  useSubscription(
    {
      query: subscriptionQuery,
      pause: address === undefined,
      variables: {
        address: address ?? "",
        filter: disableSpamFiltering ? { disableSpamFiltering } : undefined,
      },
    },
    {
      onResult: newData => {
        setBalances(prev => {
          const newBalance = readFragment(
            ContextBalanceFragment,
            newData.currencyOwnershipUpdates,
          )
          if (newBalance.quantity === "0") {
            return prev
              .filter(
                balance => balance.id !== newData.currencyOwnershipUpdates.id,
              )
              .sort((a, b) => Number(b.usdValue) - Number(a.usdValue))
          }

          if (
            prev.find(
              balance => balance.id === newData.currencyOwnershipUpdates.id,
            )
          ) {
            return prev
              .map(balance => {
                if (balance.id === newData.currencyOwnershipUpdates.id) {
                  return newBalance
                }
                return balance
              })
              .sort((a, b) => Number(b.usdValue) - Number(a.usdValue))
          }

          return [...prev, newBalance].sort(
            (a, b) => Number(b.usdValue) - Number(a.usdValue),
          )
        })
      },
    },
  )

  const value: BalancesContextType = useMemo(() => {
    return {
      balances,
      loading,
      refreshBalances: reexecuteQuery,
    }
  }, [balances, loading, reexecuteQuery])

  return (
    <BalancesContext.Provider value={value}>
      {children}
    </BalancesContext.Provider>
  )
}
